import React from "react";

/**
 * UserTable renders a filterable table of users with their scopes
 * displayed as visual badges. Provides Edit and Revoke All actions per row.
 *
 * Filtering is applied client-side based on searchQuery (email substring)
 * and scopeFilter (scope inclusion).
 *
 * Shows a loading indicator while data is being fetched, and an error
 * message with retry button on failure.
 */
export default function UserTable({
  users,
  loading,
  error,
  onRetry,
  onEdit,
  onRevoke,
  searchQuery,
  scopeFilter,
}) {
  if (error) {
    return (
      <div className="access-manager-panel__error" role="alert">
        <p className="user-table__error-message">{error}</p>
        <button
          className="user-table__retry-btn"
          onClick={onRetry}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="access-manager-panel__loading" aria-busy="true" aria-label="Loading user data">
        Loading users…
      </div>
    );
  }

  const filteredUsers = filterUsers(users, searchQuery, scopeFilter);

  if (filteredUsers.length === 0) {
    return (
      <div className="user-table__empty">
        No users found.
      </div>
    );
  }

  return (
    <table className="access-manager-panel__table" aria-label="User access table">
      <thead>
        <tr>
          <th>Email</th>
          <th>Scopes</th>
          <th>Granted By</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filteredUsers.map((user) => (
          <tr key={user.email}>
            <td>{user.email}</td>
            <td>
              <div className="user-table__scopes">
                {(user.scopes || []).map((scope) => (
                  <span key={scope} className="user-table__scope-badge">
                    {scope}
                  </span>
                ))}
              </div>
            </td>
            <td>{user.granted_by || "—"}</td>
            <td>
              <div className="user-table__actions">
                <button
                  className="user-table__action-btn user-table__action-btn--edit"
                  onClick={() => onEdit(user)}
                  type="button"
                  aria-label={`Edit scopes for ${user.email}`}
                >
                  Edit
                </button>
                <button
                  className="user-table__action-btn user-table__action-btn--revoke"
                  onClick={() => onRevoke(user)}
                  type="button"
                  aria-label={`Revoke all access for ${user.email}`}
                >
                  Revoke All
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Filters users by email substring match and scope inclusion.
 */
function filterUsers(users, searchQuery, scopeFilter) {
  if (!Array.isArray(users)) return [];

  let result = users;

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    result = result.filter((user) =>
      user.email.toLowerCase().includes(query)
    );
  }

  if (scopeFilter) {
    result = result.filter((user) =>
      (user.scopes || []).includes(scopeFilter)
    );
  }

  return result;
}
